import React, { useEffect, useState } from "react";
import { BrowserRouter, Navigate, Route, Routes } from "react-router-dom";
import { Toaster } from "sonner";
import LandingPage from "@/features/sonas/components/LandingPage";
import AppWorkspace from "@/features/sonas/components/AppWorkspace";
import AdminLayout from "@/features/admin/AdminLayout";
import Login from "@/pages/Login";
import Terms from "@/pages/Terms";
import Privacy from "@/pages/Privacy";
import GDPR from "@/pages/GDPR";
import AUP from "@/pages/AUP";
import SharedProjectView from "@/features/sonas/components/SharedProjectView";
import { useSonasTheme } from "@/features/sonas/theme";
import { AuthProvider, useAuth } from "@/integrations/supabase/AuthProvider";
import { SonasProvider } from "@/features/sonas/SonasContext";
import ErrorBoundary from "@/components/ui/error-boundary";
import { AlertCircle, Ban, Hammer } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";

const MASTER_ADMIN_EMAIL = "arsgrouproblox@gmail.com";

const ProtectedRoute = ({ children, requireAdmin = false }) => {
  const { user, profile, loading } = useAuth();
  const [maintenance, setMaintenance] = useState(false);
  const [checking, setChecking] = useState(true);
  
  useEffect(() => {
    const checkMaintenance = async () => {
      const { data } = await supabase.from('system_settings').select('value').eq('key', 'maintenance').single();
      if (data?.value === true) setMaintenance(true);
      setChecking(false);
    };
    checkMaintenance();
  }, []);

  if (loading || checking) return null;
  if (!user) return <Navigate to="/login" replace />;
  
  const isMaster = user.email === MASTER_ADMIN_EMAIL;
  const isPromotedAdmin = profile?.is_admin === true;
  const isAdmin = isMaster || isPromotedAdmin;

  // Maintenance Check
  if (maintenance && !isAdmin) {
    return (
      <div className="sonas-page flex flex-col items-center justify-center p-6 text-center">
        <div className="liquid-glass p-12 rounded-[48px] max-w-md space-y-6">
          <div className="h-16 w-16 bg-amber-500/10 rounded-3xl flex items-center justify-center mx-auto border border-amber-500/20">
            <Hammer className="h-8 w-8 text-amber-500" />
          </div>
          <h1 className="text-3xl font-syne font-bold">Maintenance Mode</h1>
          <p className="text-sonas-muted text-sm leading-relaxed">
            Architects are currently re-calibrating the workspace grid. We'll be back online shortly.
          </p>
          <div className="pt-4">
            <button onClick={() => window.location.href = '/'} className="text-[10px] font-bold uppercase tracking-widest text-sonas-dim hover:text-sonas-accent">Return to Grid</button>
          </div>
        </div>
      </div>
    );
  }

  // Suspension Check
  if (profile?.is_suspended) {
    return (
      <div className="sonas-page flex flex-col items-center justify-center p-6 text-center">
        <div className="liquid-glass p-12 rounded-[48px] max-w-md space-y-6">
          <div className="h-16 w-16 bg-red-500/10 rounded-3xl flex items-center justify-center mx-auto border border-red-500/20">
            <Ban className="h-8 w-8 text-red-500" />
          </div>
          <h1 className="text-3xl font-syne font-bold">Node Isolated</h1>
          <p className="text-sonas-muted text-sm leading-relaxed">
            Your architect node has been suspended for violating platform engineering standards.
          </p>
          <div className="pt-4">
            <button onClick={() => window.location.href = '/'} className="text-[10px] font-bold uppercase tracking-widest text-sonas-dim hover:text-sonas-accent">Return to Grid</button>
          </div>
        </div>
      </div>
    );
  }

  if (requireAdmin && !isAdmin) {
    return <Navigate to="/app" replace />;
  }
  
  return children;
};

function App() {
  const { themeMode, resolvedTheme, setThemeMode } = useSonasTheme();

  return (
    <div className="App">
      <AuthProvider>
        <SonasProvider>
          <BrowserRouter>
            <Routes>
              <Route path="/" element={<LandingPage />} />
              <Route path="/login" element={<Login />} />
              <Route path="/terms" element={<Terms />} />
              <Route path="/privacy" element={<Privacy />} />
              <Route path="/gdpr" element={<GDPR />} />
              <Route path="/aup" element={<AUP />} />
              <Route path="/share/:shareId" element={<SharedProjectView />} />
              
              <Route
                path="/app/*"
                element={
                  <ProtectedRoute>
                    <ErrorBoundary>
                      <AppWorkspace themeMode={themeMode} resolvedTheme={resolvedTheme} setThemeMode={setThemeMode} />
                    </ErrorBoundary>
                  </ProtectedRoute>
                }
              />

              <Route
                path="/admin/*"
                element={
                  <ProtectedRoute requireAdmin={true}>
                    <ErrorBoundary>
                      <AdminLayout />
                    </ErrorBoundary>
                  </ProtectedRoute>
                }
              />

              <Route path="*" element={<Navigate to="/" replace />} />
            </Routes>
          </BrowserRouter>
          <Toaster richColors position="top-right" />
        </SonasProvider>
      </AuthProvider>
    </div>
  );
}

export default App;